import React, { useState } from 'react';
import { Send, Mail, User, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const MIN_MESSAGE_LENGTH = 10;
const MAX_MESSAGE_LENGTH = 1000;
const SUBMIT_COOLDOWN_MS = 30 * 1000;
const LAST_SUBMIT_STORAGE_KEY = 'portfolio-contact-last-submit';

if (process.env.NODE_ENV === 'development') {
  console.info('Gotcha looking');
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const web3FormsAccessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
  const showSuccessMessage = submitStatus === 'success';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setErrorMessage('');

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedMessage = formData.message.trim();

    if (trimmedName.length < 1 || trimmedName.length > 100) {
      setSubmitStatus('error');
      setErrorMessage('Name must be between 1 and 100 characters.');
      return;
    }

    if (trimmedMessage.length < MIN_MESSAGE_LENGTH || trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      setSubmitStatus('error');
      setErrorMessage(`Message must be between ${MIN_MESSAGE_LENGTH} and ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    const lastSubmit = Number(window.localStorage.getItem(LAST_SUBMIT_STORAGE_KEY));
    const remainingCooldown = SUBMIT_COOLDOWN_MS - (Date.now() - lastSubmit);

    if (lastSubmit && remainingCooldown > 0) {
      setSubmitStatus('error');
      setErrorMessage(`Please wait ${Math.ceil(remainingCooldown / 1000)} seconds before sending another message.`);
      return;
    }

    if (!web3FormsAccessKey) {
      setSubmitStatus('error');
      setErrorMessage('Web3Forms is not configured yet. Add REACT_APP_WEB3FORMS_ACCESS_KEY to the frontend env.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: web3FormsAccessKey,
          name: trimmedName,
          email: trimmedEmail,
          replyto: trimmedEmail,
          message: trimmedMessage,
          subject: `New portfolio message from ${trimmedName}`,
          botcheck: '',
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Web3Forms request failed');
      }

      // Success
      setSubmitStatus('success');
      setErrorMessage('');
      setFormData({ name: '', email: '', message: '' });
      window.localStorage.setItem(LAST_SUBMIT_STORAGE_KEY, String(Date.now()));
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Web3Forms Error:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error && error.message
        ? error.message
        : 'Failed to send message. Please try again or email me directly.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Get In Touch</h2>
          <div className="w-20 h-1 bg-[#BCA4E3] mx-auto mb-6"></div>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            I'm actively seeking internship opportunities in DevOps, Cloud engineering, and IT automation.
            Feel free to reach out if you'd like to connect!
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Name
                </div>
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                maxLength={100}
                placeholder="Your name"
                className="w-full bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#BCA4E3] focus:ring-1 focus:ring-[#BCA4E3]"
                disabled={isSubmitting}
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </div>
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                className="w-full bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#BCA4E3] focus:ring-1 focus:ring-[#BCA4E3]"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                <div className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Message
                </div>
              </label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                minLength={MIN_MESSAGE_LENGTH}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Your message..."
                rows={6}
                className="w-full bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#BCA4E3] focus:ring-1 focus:ring-[#BCA4E3]"
                disabled={isSubmitting}
              />
            </div>

            <input type="checkbox" name="botcheck" className="hidden" tabIndex="-1" autoComplete="off" />

            {/* Status Messages */}
            {showSuccessMessage && (
              <div className="!mt-8 flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>Message sent successfully! I'll get back to you soon.</span>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{errorMessage || 'Failed to send message. Please try again or email me directly.'}</span>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              className="w-full group bg-[#BCA4E3] hover:bg-[#A88DD6] text-slate-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

        </div>
      </div>
    </section>
  );
};

export default Contact;
